import { useRef, useCallback } from 'react';

const RECEIPT_WIDTH = '80mm';

const receiptStyles = `
  @media print {
    body * { visibility: hidden !important; }
    #receipt-print-area, #receipt-print-area * { visibility: visible !important; }
    #receipt-print-area {
      position: fixed !important;
      left: 0 !important;
      top: 0 !important;
      width: ${RECEIPT_WIDTH} !important;
      margin: 0 !important;
      padding: 2mm !important;
      font-family: 'Courier New', monospace !important;
      font-size: 12px !important;
      color: #000 !important;
      background: #fff !important;
    }
    @page {
      size: ${RECEIPT_WIDTH} auto;
      margin: 0;
    }
  }
`;

function formatPrice(val) {
  return Number(val || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

export function ReceiptContent({ order, businessName = 'AkıllıÇözüm', showKitchen = false }) {
  if (!order) return null;

  const dashes = '------------------------------------------------';

  return (
    <div
      id="receipt-print-area"
      data-testid="receipt-content"
      style={{
        width: RECEIPT_WIDTH,
        fontFamily: "'Courier New', monospace",
        fontSize: '12px',
        color: '#000',
        background: '#fff',
        padding: '2mm',
        lineHeight: '1.4',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '4px' }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{businessName}</div>
        {showKitchen && <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '2px' }}>--- MUTFAK FİŞİ ---</div>}
        {!showKitchen && <div style={{ fontSize: '11px', marginTop: '2px' }}>SİPARİŞ FİŞİ</div>}
      </div>

      <div style={{ fontSize: '10px', textAlign: 'center' }}>{dashes}</div>

      {/* Order info */}
      <div style={{ marginBottom: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Fiş No:</span>
          <span style={{ fontWeight: 'bold' }}>#{(order.id || '').slice(0, 8).toUpperCase()}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Tarih:</span>
          <span>{formatDate(order.created_at)}</span>
        </div>
        {order.table_no && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Masa:</span>
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{order.table_no}</span>
          </div>
        )}
        {order.created_by && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Kasiyer:</span>
            <span>{order.created_by}</span>
          </div>
        )}
      </div>

      <div style={{ fontSize: '10px', textAlign: 'center' }}>{dashes}</div>

      {/* Items */}
      <div style={{ marginBottom: '4px' }}>
        {showKitchen ? (
          // Kitchen receipt - large text, no prices
          <div>
            {(order.items || []).map((item, i) => (
              <div key={i} style={{ padding: '3px 0', borderBottom: '1px dashed #ccc' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  {item.quantity}x {item.name}
                </div>
                {item.variant && (
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#666' }}>
                    &nbsp;&nbsp;({item.variant})
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Customer receipt - with prices
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #000' }}>
                <th style={{ textAlign: 'left', padding: '2px 0' }}>Ürün</th>
                <th style={{ textAlign: 'center', padding: '2px', width: '30px' }}>Ad.</th>
                <th style={{ textAlign: 'right', padding: '2px 0', width: '60px' }}>Tutar</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item, i) => (
                <tr key={i}>
                  <td style={{ padding: '2px 0' }}>
                    {item.name}
                    {item.variant ? ` (${item.variant})` : ''}
                  </td>
                  <td style={{ textAlign: 'center', padding: '2px' }}>{item.quantity}</td>
                  <td style={{ textAlign: 'right', padding: '2px 0', fontWeight: 'bold' }}>
                    {formatPrice(item.price * item.quantity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ fontSize: '10px', textAlign: 'center' }}>{dashes}</div>

      {/* Total - only for customer receipt */}
      {!showKitchen && (
        <div style={{ marginBottom: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold', padding: '4px 0' }}>
            <span>TOPLAM:</span>
            <span>{formatPrice(order.total)} TL</span>
          </div>
        </div>
      )}

      {/* Note */}
      {order.note && (
        <>
          <div style={{ fontSize: '10px', textAlign: 'center' }}>{dashes}</div>
          <div style={{ padding: '2px 0', fontSize: '11px' }}>
            <span style={{ fontWeight: 'bold' }}>Not: </span>{order.note}
          </div>
        </>
      )}

      <div style={{ fontSize: '10px', textAlign: 'center' }}>{dashes}</div>

      {/* Footer */}
      {!showKitchen && (
        <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '4px' }}>
          <div>Teşekkür ederiz!</div>
          <div>Afiyet olsun</div>
        </div>
      )}

      {showKitchen && order.note && (
        <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 'bold', marginTop: '4px', padding: '4px', border: '2px solid #000' }}>
          NOT: {order.note}
        </div>
      )}
    </div>
  );
}

export function useReceiptPrinter() {
  const styleRef = useRef(null);

  const printReceipt = useCallback((order, options = {}) => {
    const { businessName = 'AkıllıÇözüm', showKitchen = false } = options;

    // Create print container
    const printContainer = document.createElement('div');
    printContainer.id = 'receipt-print-area';

    // Build receipt HTML
    const dashes = '------------------------------------------------';
    const items = order.items || [];

    let itemsHtml = '';
    if (showKitchen) {
      itemsHtml = items.map(item =>
        `<div style="padding:3px 0;border-bottom:1px dashed #ccc">
          <div style="font-size:16px;font-weight:bold">${item.quantity}x ${item.name}</div>
          ${item.variant ? `<div style="font-size:14px;font-weight:bold;color:#666">&nbsp;&nbsp;(${item.variant})</div>` : ''}
        </div>`
      ).join('');
    } else {
      itemsHtml = `<table style="width:100%;border-collapse:collapse;font-size:11px">
        <thead><tr style="border-bottom:1px solid #000">
          <th style="text-align:left;padding:2px 0">Ürün</th>
          <th style="text-align:center;padding:2px;width:30px">Ad.</th>
          <th style="text-align:right;padding:2px 0;width:60px">Tutar</th>
        </tr></thead><tbody>` +
        items.map(item =>
          `<tr><td style="padding:2px 0">${item.name}${item.variant ? ` (${item.variant})` : ''}</td>
          <td style="text-align:center;padding:2px">${item.quantity}</td>
          <td style="text-align:right;padding:2px 0;font-weight:bold">${formatPrice(item.price * item.quantity)}</td></tr>`
        ).join('') +
        `</tbody></table>`;
    }

    printContainer.innerHTML = `
      <div style="text-align:center;margin-bottom:4px">
        <div style="font-size:16px;font-weight:bold">${businessName}</div>
        <div style="font-size:${showKitchen ? '14' : '11'}px;margin-top:2px;${showKitchen ? 'font-weight:bold' : ''}">${showKitchen ? '--- MUTFAK FİŞİ ---' : 'SİPARİŞ FİŞİ'}</div>
      </div>
      <div style="font-size:10px;text-align:center">${dashes}</div>
      <div style="margin-bottom:4px">
        <div style="display:flex;justify-content:space-between"><span>Fiş No:</span><span style="font-weight:bold">#${(order.id || '').slice(0, 8).toUpperCase()}</span></div>
        <div style="display:flex;justify-content:space-between"><span>Tarih:</span><span>${formatDate(order.created_at)}</span></div>
        ${order.table_no ? `<div style="display:flex;justify-content:space-between"><span>Masa:</span><span style="font-weight:bold;font-size:14px">${order.table_no}</span></div>` : ''}
        ${order.created_by ? `<div style="display:flex;justify-content:space-between"><span>Kasiyer:</span><span>${order.created_by}</span></div>` : ''}
      </div>
      <div style="font-size:10px;text-align:center">${dashes}</div>
      <div style="margin-bottom:4px">${itemsHtml}</div>
      <div style="font-size:10px;text-align:center">${dashes}</div>
      ${!showKitchen ? `<div style="margin-bottom:4px"><div style="display:flex;justify-content:space-between;font-size:16px;font-weight:bold;padding:4px 0"><span>TOPLAM:</span><span>${formatPrice(order.total)} TL</span></div></div>` : ''}
      ${order.note ? `<div style="font-size:10px;text-align:center">${dashes}</div><div style="padding:2px 0;font-size:${showKitchen ? '14' : '11'}px;${showKitchen ? 'font-weight:bold;text-align:center;padding:4px;border:2px solid #000' : ''}"><span style="font-weight:bold">${showKitchen ? 'NOT: ' : 'Not: '}</span>${order.note}</div>` : ''}
      ${!showKitchen ? `<div style="font-size:10px;text-align:center">${dashes}</div><div style="text-align:center;font-size:10px;margin-top:4px"><div>Teşekkür ederiz!</div><div>Afiyet olsun</div></div>` : ''}
    `;

    // Add print styles
    if (!styleRef.current) {
      styleRef.current = document.createElement('style');
      styleRef.current.textContent = receiptStyles;
      document.head.appendChild(styleRef.current);
    }

    // Add to DOM, print, then remove
    document.body.appendChild(printContainer);

    // Apply inline styles for print
    Object.assign(printContainer.style, {
      width: RECEIPT_WIDTH,
      fontFamily: "'Courier New', monospace",
      fontSize: '12px',
      color: '#000',
      background: '#fff',
      padding: '2mm',
      lineHeight: '1.4',
      position: 'fixed',
      left: '0',
      top: '0',
      zIndex: '99999',
    });

    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.removeChild(printContainer);
      }, 500);
    }, 100);
  }, []);

  return { printReceipt };
}
